/**
 * The timeline EDIT LEASE (2026-10-20): one device edits a timeline at a time.
 *
 * Our operator, 2026-09-23 (DEC-212): *"if editting, putting a lock on any other device editting is a good idea"*,
 * with *"the option to discard the other session, or take over, or keep it"*. OPBridge built it in S329, modelled on
 * OPVisual's project lock (`opbridge-core/src/timeline_lease.rs`, read 2026-10-20):
 *   - `POST /timelines/opbuttons/{id}/lease {holder, holderName, mode}`, `mode` open / renew / takeover / discard;
 *     answers `{held, holder, lost, renewSecs, freshSecs}`. Each device writes its own claim; the winner is computed
 *     (the latest take-over, else whoever opened first), so every bridge agrees.
 *   - STICKY: a device that opened while another held it stays locked out until it opens again, even after the
 *     holder leaves (its copy is older by then). So "Keep" is not taking over, and "Reopen" comes when they leave.
 *   - A timeline PUT carrying `X-OPBridge-Lease-Holder` from a device that does not hold it answers 409 `lease_held`.
 *   - No capability names it: a bridge from before S329 answers the route 404, and then nothing here changes anything.
 *
 * Here: the editor opens a lease when it opens a timeline and lets go when it closes (`TimelineV2.tsx`); while held
 * it renews every `renewSecs` and every push carries the header (`bridgeSync.ts`); while locked out the store refuses
 * local edits to that timeline (`store.ts::upsertTimeline`) and the editor shows who is editing, with Take over,
 * Discard theirs and Keep, then Reopen once they have left.
 */
import { createSignal } from 'solid-js';
import { getConnection, resolveBridges } from '../opbridge/store.ts';
import { deviceId } from '../opbridge/storage.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';

const CONSUMER = 'opbuttons';

export interface LeaseState {
  timelineId: string;
  bridgeId: string;
  /** False when that bridge has no lease route (before S329): nothing is locked. */
  supported: boolean;
  held: boolean;
  /** Who holds it when we do not (their device name). */
  holderName: string | null;
  /** How we lost it, when another device took it from us. */
  lost: { mode: string; byName: string } | null;
  /** Locked out and the holder has gone: Reopen will take it. */
  holderGone: boolean;
}

const [leases, setLeases] = createSignal<ReadonlyMap<string, LeaseState>>(new Map());
const timers = new Map<string, ReturnType<typeof setInterval>>();

/** This device as a lease holder: OPBridge takes 1-64 of `A-Z a-z 0-9 _ -` with no `--`; our device id is a UUID. */
export function leaseHolder(): string {
  return deviceId().replace(/[^A-Za-z0-9_-]/g, '').replace(/-{2,}/g, '-').slice(0, 64) || 'opbuttons';
}
/** What another device calls this one ("iPad", "Mac", "Windows PC"). */
export function leaseHolderName(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const touchMac = /Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
  return /iPad/.test(ua) || touchMac ? 'iPad' : /iPhone/.test(ua) ? 'iPhone' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows PC' : /Android/.test(ua) ? 'Android' : 'another device';
}

export const leaseFor = (timelineId: string): LeaseState | null => leases().get(timelineId) ?? null;
/** True while this device is locked out of editing `timelineId` (another device holds its lease). */
export function editLocked(timelineId: string): boolean {
  const l = leases().get(timelineId);
  return !!l && l.supported && !l.held;
}
/** The header value a push of `timelineId` to `bridgeId` carries: our holder id while we hold it there - and ONLY
 *  where OPBridge says `timeline_lease`. Measured 2026-10-20 on the dev PC (`OPTIONS` preflight to the LAN address the
 *  app uses): S329's CORS allow-list is `authorization, content-type, x-opbridge-secret`, so a browser that sent
 *  `X-OPBridge-Lease-Holder` would have every timeline PUT refused at the preflight. Asked of OPBridge (allow the
 *  header, advertise `timeline_lease`); until then the lease still claims, renews and locks this editor, and OPBridge
 *  checks nothing on a PUT. */
export function leaseHeaderFor(timelineId: string, bridgeId: string): string | null {
  const l = leases().get(timelineId);
  // Only the computer the lease was opened on, also now that a timeline's parts go to several (2026-10-28): another
  // computer may not have the lease's latest claim yet (they replicate as docs), and a refusal there would lock this
  // editor out for nothing. A displaced editor learns on its next renew (within seconds) either way.
  if (!l || !l.supported || !l.held || l.bridgeId !== bridgeId) return null;
  return getConnection(bridgeId)?.health()?.capabilities?.['timeline_lease'] === true ? leaseHolder() : null;
}

function put(state: LeaseState): void {
  const m = new Map(leases()); m.set(state.timelineId, state); setLeases(m);
}
function drop(timelineId: string): void {
  const m = new Map(leases()); m.delete(timelineId); setLeases(m);
  const t = timers.get(timelineId); if (t) clearInterval(t); timers.delete(timelineId);
}

type Answer = { held: boolean; holder: { holder: string; holderName: string } | null; lost: { mode: string; by: string; byName: string } | null; renewSecs?: number };

async function ask(bridgeId: string, timelineId: string, mode: 'open' | 'renew' | 'takeover' | 'discard'): Promise<Answer | 'unsupported' | null> {
  const conn = getConnection(bridgeId);
  if (!conn || conn.status() !== 'connected') return null;
  try {
    const a = await conn.httpClient().leaseTimeline(CONSUMER, timelineId, { holder: leaseHolder(), holderName: leaseHolderName(), mode }) as Answer;
    // Anything that is not a lease answer is no lease: never lock the editor on a reply we do not understand.
    return a && typeof a.held === 'boolean' ? a : 'unsupported';
  } catch (err) {
    if (err instanceof OpbridgeError && (err.httpStatus === 404 || err.httpStatus === 405)) return 'unsupported';
    return null;
  }
}

function apply(timelineId: string, bridgeId: string, a: Answer, wasHeld: boolean): void {
  const lostNow = wasHeld && !a.held;
  put({
    timelineId, bridgeId, supported: true, held: a.held,
    holderName: a.held ? null : a.holder?.holderName || (a.holder ? 'another device' : null),
    lost: lostNow && a.lost ? { mode: a.lost.mode, byName: a.lost.byName || 'another device' } : (leases().get(timelineId)?.lost ?? null),
    holderGone: !a.held && !a.holder,
  });
}

/** Renew while held; while locked out, watch for the holder leaving (so Reopen can be offered). */
function tick(timelineId: string, bridgeId: string, renewSecs: number): void {
  const t = timers.get(timelineId); if (t) clearInterval(t);
  timers.set(timelineId, setInterval(() => {
    const cur = leases().get(timelineId);
    if (!cur) return;
    if (cur.held) {
      void ask(bridgeId, timelineId, 'renew').then((a) => { if (a && a !== 'unsupported' && leases().get(timelineId)) apply(timelineId, bridgeId, a, true); });
    } else {
      void getConnection(bridgeId)?.httpClient().leaseState(CONSUMER, timelineId).then((s) => {
        const c = leases().get(timelineId);
        if (c && !c.held) put({ ...c, holderGone: !s.holder, holderName: s.holder?.holderName || c.holderName });
      }).catch(() => {});
    }
  }, Math.max(2, renewSecs) * 1000));
}

/** The editor opened (or REOPENED) `timelineId`: claim its lease on its home bridge. */
export async function openLease(timelineId: string, target = ''): Promise<void> {
  const cfg = resolveBridges(target, { ignorePanicLatch: true })[0];
  if (!cfg || !timelineId) return;
  const a = await ask(cfg.id, timelineId, 'open');
  if (a === 'unsupported') { put({ timelineId, bridgeId: cfg.id, supported: false, held: false, holderName: null, lost: null, holderGone: false }); return; }
  if (!a) return; // offline: no lease, nothing locked (DEC-212's modifiedAt stays the backstop)
  apply(timelineId, cfg.id, a, false);
  tick(timelineId, cfg.id, a.renewSecs ?? 10);
}

/** Take over (keep editing from the latest) or discard the other session: either way this device holds it. */
export async function takeLease(timelineId: string, mode: 'takeover' | 'discard'): Promise<boolean> {
  const cur = leases().get(timelineId);
  if (!cur) return false;
  const a = await ask(cur.bridgeId, timelineId, mode);
  if (!a || a === 'unsupported') return false;
  put({ ...cur, held: a.held, holderName: null, lost: null, holderGone: false });
  return a.held;
}

/** The editor closed: let go. */
export function closeLease(timelineId: string): void {
  const cur = leases().get(timelineId);
  drop(timelineId);
  if (!cur || !cur.supported) return;
  void getConnection(cur.bridgeId)?.httpClient().releaseLease(CONSUMER, timelineId, leaseHolder()).catch(() => {});
}

/** A push came back 409 `lease_held`: we do not hold it any more. */
export function noteLeaseRefused(timelineId: string, holderName: string | null): void {
  const cur = leases().get(timelineId);
  if (cur) put({ ...cur, held: false, holderName: holderName ?? cur.holderName ?? 'another device', lost: cur.lost ?? { mode: 'takeover', byName: holderName ?? 'another device' } });
}

/** For the smoke: what this device holds. */
export const leasesForTest = leases;
